import { useEffect } from "react";

import { absUrl, DEFAULT_OG_IMAGE, DEFAULT_TITLE, fitTitle } from "@/seo/shared";

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/** Canonical y robots para la ruta actual. Lo usa useDocumentMeta y también
 *  SeoRouteReset al navegar, para que ninguna página herede los de la anterior
 *  (ni los del HTML prerenderizado con el que se aterrizó). */
export function applyRobots(opts: { noindex?: boolean; path?: string }) {
  const canonical = document.head.querySelector('link[rel="canonical"]');
  if (opts.noindex) {
    upsertMeta("name", "robots", "noindex, follow");
    canonical?.remove();
    return;
  }
  document.head.querySelector('meta[name="robots"]')?.remove();
  const url = absUrl(opts.path ?? window.location.pathname);
  upsertLink("canonical", url);
  upsertMeta("property", "og:url", url);
}

/**
 * Meta por ruta en la SPA: <title>, description, Open Graph, Twitter, canonical
 * y robots. Las páginas públicas llegan ya con esta cabecera en el HTML
 * prerenderizado (scripts/prerender.mjs, mismos builders de src/seo/shared.ts);
 * el hook la mantiene al navegar dentro de la SPA.
 *
 * `enabled: false` mientras cargan los datos: así no se pisa la cabecera
 * prerenderizada con valores por defecto. `noindex` quita el canonical y pone
 * robots noindex; sin él se retira cualquier robots previo (p. ej. el del shell
 * de fichas `_ficha.html`).
 */
export function useDocumentMeta(opts: {
  title?: string;
  /** Título final ya ajustado (p. ej. con movieCityMeta); si no, fitTitle([title]). */
  fullTitle?: string;
  description?: string;
  image?: string;
  /** Ruta canónica; por defecto la actual. */
  path?: string;
  noindex?: boolean;
  enabled?: boolean;
}) {
  const { title, fullTitle, description, image, path, noindex = false, enabled = true } = opts;
  useEffect(() => {
    if (!enabled) return;
    const t = fullTitle ?? (title ? fitTitle([title]) : DEFAULT_TITLE);
    document.title = t;
    upsertMeta("property", "og:title", t);
    upsertMeta("name", "twitter:title", t);

    if (description) {
      upsertMeta("name", "description", description);
      upsertMeta("property", "og:description", description);
      upsertMeta("name", "twitter:description", description);
    }

    applyRobots({ noindex, path });

    upsertMeta("property", "og:image", image || DEFAULT_OG_IMAGE);
    upsertMeta("name", "twitter:image", image || DEFAULT_OG_IMAGE);
  }, [title, fullTitle, description, image, path, noindex, enabled]);
}

/** Retira el JSON-LD que venía en el HTML prerenderizado. */
export function removePrerenderedJsonLd() {
  document.head
    .querySelectorAll('script[type="application/ld+json"][data-prerender]')
    .forEach((el) => el.remove());
}

/** Inyecta un bloque JSON-LD (schema.org) y lo retira al desmontar la página.
 *  Sustituye al del prerender: nunca conviven dos versiones del mismo grafo. */
export function useJsonLd(data: Record<string, unknown> | null) {
  const json = data ? JSON.stringify(data) : null;
  useEffect(() => {
    if (!json) return;
    removePrerenderedJsonLd();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-seo", "jsonld");
    script.textContent = json;
    document.head.appendChild(script);
    return () => {
      script.remove();
    };
  }, [json]);
}
